import { forwardRef, JSX, useImperativeHandle } from 'react';
import ValidationInfo from '../../../validation/validation-info';
import { IdHelper } from '../../../helpers/id.helper';
import RequiredAsterisk from '../RequiredAsterisk';
import './style.scss';
import type IValidatable from '../validatable.interface';
import ValidationEngine from '../../../validation/validation-engine';
import type { IServerValidationError } from '../validatable.interface';
import { SanitizerService } from '../../../services/sanitizer.service';

export interface ICheckboxFormGroupState {
  value: string[];
  validationInfo: ValidationInfo;
}

export interface ICheckboxFormGroupProps {
  id?: string;
  className?: string;
  serverValidationName?: string;
  labelText?: string;
  hideLabel?: boolean;
  ariaDescribedby?: string;
  helpTextElement?: JSX.Element;
  rowLayoutMdUp?: boolean;
  options: { value: string; text: string; iconElement?: JSX.Element }[];
  required?: boolean;
  allowUnsafeHtml?: boolean;
  state: ICheckboxFormGroupState;
  onChange?: (value: string[]) => void;
}

export default forwardRef<IValidatable, ICheckboxFormGroupProps>(function CheckboxFormGroup(
  {
    id,
    className,
    serverValidationName = undefined,
    labelText,
    hideLabel = false,
    ariaDescribedby,
    helpTextElement,
    rowLayoutMdUp = false,
    options,
    required = false,
    allowUnsafeHtml = false,
    state,
    onChange,
  }: ICheckboxFormGroupProps,
  ref
) {
  const idWithFallback = id ?? IdHelper.newUniqueId('checkbox-group-form-group-');
  const helpTextId = helpTextElement ? `${id}-help` : undefined;

  // === INTERFACE IMPLEMENTATION ===
  useImperativeHandle(ref, () => ({
    checkValidity(): ValidationInfo {
      const updatedValidationInfo = new ValidationInfo(`${labelText} - `);
      ValidationEngine.for(updatedValidationInfo).if(required).notEmpty(state.value).endif().validate();
      return updatedValidationInfo;
    },
    handleServerValidationErrors(errors: IServerValidationError[]): ValidationInfo {
      return new ValidationInfo(
        `${labelText} - `,
        errors
          .filter((e) => e.name === serverValidationName)
          .map((e) => e.errorMessages)
          .reduce((a, b) => a.concat(b), [])
      );
    },
  }));

  // === EVENT HANDLERS ===
  const handleCheckboxChange = (optionValue: string) => {
    if (state.value.includes(optionValue)) {
      onChange?.(state.value.filter((value) => value !== optionValue));
    } else {
      onChange?.([...state.value, optionValue]);
    }
  };

  return (
    <fieldset className={`checkbox-group-form-group mb-3 ${className ?? ''}`}>
      {labelText && (
        <legend className={hideLabel ? 'visually-hidden' : ''}>
          {labelText}
          {required && <RequiredAsterisk />}
        </legend>
      )}
      <div className={`${rowLayoutMdUp ? 'd-flex flex-wrap flex-column flex-sm-row' : ''} mb-md-0`}>
        {options.map(({ value, text, iconElement }, index) => (
          <div key={value} className={`form-check ${state.validationInfo.isInvalid ? 'is-invalid' : ''} ${index < options.length - 1 ? 'mb-2' : 'mb-0'} ${rowLayoutMdUp ? 'me-sm-3 mb-md-0' : ''}`}>
            <input
              type="checkbox"
              className={`form-check-input ${state.validationInfo.isInvalid ? 'is-invalid' : ''}`}
              name={idWithFallback}
              id={`${idWithFallback}-${value.toLocaleLowerCase()}`}
              value={value}
              checked={state.value.includes(value)}
              onChange={() => {
                handleCheckboxChange(value);
              }}
              aria-invalid={state.validationInfo.isInvalid}
              aria-describedby={state.validationInfo.isInvalid ? `${idWithFallback}-validation-error` : ariaDescribedby}
            />
            <label className="form-check-label d-flex align-items-center" htmlFor={`${idWithFallback}-${value.toLocaleLowerCase()}`}>
              {iconElement}
              {allowUnsafeHtml && <span dangerouslySetInnerHTML={{ __html: SanitizerService.sanitizeHtml(text) }} />}
              {!allowUnsafeHtml && <span>{text}</span>}
            </label>
          </div>
        ))}
        {state.validationInfo.isInvalid && (
          <div id={`${idWithFallback}-validation-error`} className="invalid-feedback">
            {state.validationInfo.errors.map((error) => (
              <div key={error}>{error}</div>
            ))}
          </div>
        )}
        {helpTextElement && (
          <div className="form-text">
            <div id={helpTextId}>{helpTextElement}</div>
          </div>
        )}
      </div>
    </fieldset>
  );
});
